import React, { useRef } from 'react';
import { useAsync, useUnmount } from 'react-use';
import { cx as classNames } from '@linaria/core';
import { isNil } from 'lodash';
import { isMaskShapeProps } from 'utils/typeguards/predictionOutputs';
import { recolorMask } from 'utils/maskRecoloring/maskRecoloring';
import { thumbWrapper } from 'components/Canvas/RegionThumb/RegionThumb.styles';
import { RegionThumbProps } from 'components/Canvas/RegionThumb/common';

export const MaskRegionThumb: React.FC<RegionThumbProps> = ({ regionData, imageData, children, className, id, size }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement & { width: number; height: number }>(null);
  const recoloredImgUrl = useRef<string>('');

  useAsync(async () => {
    if (regionData && isMaskShapeProps(regionData) && imageData && ('image' in imageData || imageData.video)) {
      const container = containerRef.current;
      const canvas = canvasRef.current as HTMLCanvasElement;
      const {
        mask: {
          image: { base64 },
        },
        color,
      } = regionData;
      if (container && canvas && base64) {
        const b64 = `data:image/png;base64,${base64}`;
        recoloredImgUrl.current = await recolorMask(b64, color);

        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.addEventListener('load', () => {
          ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
        });
        img.src = recoloredImgUrl.current;
      }
    }
  }, [imageData]);

  useUnmount(() => {
    URL.revokeObjectURL(recoloredImgUrl.current);
  });

  return (
    <div ref={containerRef} className={classNames('region-thumb', className, thumbWrapper)} role="presentation">
      {children}
      <canvas
        data-testid={`region-thumb-${id}`}
        ref={canvasRef}
        {...(!isNil(size) && {
          style: {
            height: size,
            width: size,
          },
        })}
      />
    </div>
  );
};
